import PropTypes from "prop-types";
import { useContext, useEffect, useState, useMemo } from "react";
import LandingPageListing from "app/pages/LandingPage/LandingPageListing/LandingPageListing";
import { forceCheck } from "react-lazyload";
import { acceptCookyPolicy } from "app/pages/.shared/CookiePolicyFooter/cookiePolicyActionCreators";
import { clearProduct } from "app/pages/Listing/productActionCreators";
import {
	getProductsOfLandingPage,
	shouldLandingPageHideProductPriceBanner,
	getLandingPageMeta,
	getCurrentLandingPage,
} from "app/pages/LandingPage/landingPageSelector";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { fetchLandingPage } from "app/pages/LandingPage/landingPageActionCreators";
import { getPartnerListingSEO, getPartnerMarketingHomeBlocks } from "app/reducers/partnerSelector";
import { resetAllProductsFilter } from "app/pages/Listing/ProductFilters/productsFilterActionCreators";
import AppGlobalsContext from "app/AppGlobalsContext";
import { resetAllSDPProductsFilter } from "app/pages/SmartDP/Listing/SmartDPFilters/smartDPProductsFilterActionCreators";
import { updateSDPFormDestination } from "app/pages/Booking/bookingActionCreators";
import {
	SDP_SEARCH_MARKETING_BLOCKS,
	PRODUCT_EXPERIENCES,
	PRODUCTS_FILTERS_KEY,
	PRODUCT_TAG_CODES,
	MULTI_STAGE_JOURNEY_OPTIONS,
	OFFER_TYPE_LABELS,
} from "app/constants";
import { FilterProvider, OPTION_MODE } from "app/features/product/productFilters";
import "./LandingPage.scss";
import { useIntl } from "react-intl";
import { SortProvider } from "app/features/product/productSort";

const CUSTOM_COMPARATORS = {
	recommendation: (a, b) => (a?.rank || 0) - (b?.rank || 0),
	price_asc: (a, b) => (a?.fromPriceType?.value ?? 0) - (b?.fromPriceType?.value ?? 0),
	price_desc: (a, b) => (b?.fromPriceType?.value ?? 0) - (a?.fromPriceType?.value ?? 0),
};

const LandingPageContainer = props => {
	const {
		activeLandingPageCode,
		acceptCookyPolicy,
		fetchLandingPage,
		currentLandingPage,
		resetAllProductsFilter,
		updateSDPFormDestination,
		clearBooking = () => {},
		destinations = [],
		productsOfLandingPage,
	} = props;

	const [loading, setLoading] = useState(false);
	const { formatMessage, formatDate } = useIntl();

	const { shop, productsVisibility, partnerCode } = useContext(AppGlobalsContext);

	useEffect(() => {
		resetAllProductsFilter();
		clearBooking();
		if (activeLandingPageCode) {
			fetchLandingPage({
				landingPageCode: activeLandingPageCode,
				shop,
				productsVisibility,
				partnerCode,
			}).then(() => {
				setLoading(false);
				forceCheck();
			});
		}

		return () => {
			acceptCookyPolicy();
		};
	}, [activeLandingPageCode]);

	const landingPageDestinationResort = currentLandingPage?.destinationFilterValues
		? destinations.find(
				destination => destination.label === currentLandingPage?.destinationFilterValues[0]
		  )
		: undefined;

	useEffect(() => {
		const hideSmartDPForm = currentLandingPage?.layout?.hideSmartDPForm;

		if (
			!hideSmartDPForm &&
			currentLandingPage?.destinationFilterValues?.length === 1 &&
			landingPageDestinationResort
		) {
			updateSDPFormDestination({
				code: landingPageDestinationResort.code.toString(),
				label: landingPageDestinationResort.label,
			});
		}
	}, [currentLandingPage?.code, landingPageDestinationResort?.code]);

	const shouldShowMultiStageJourneyOption = useMemo(
		() =>
			productsOfLandingPage?.some(product =>
				[
					PRODUCT_EXPERIENCES.CIRCUIT,
					PRODUCT_EXPERIENCES.AUTOTOUR,
					PRODUCT_EXPERIENCES.CRUISE,
				].includes(product?.productExperience)
			),
		[productsOfLandingPage]
	);

	const facetConfigs = useMemo(
		() => [
			{
				field: PRODUCTS_FILTERS_KEY.DESTINATION,
				title: formatMessage({ id: "product.page.filter.destinations.label" }),
				valueGetter: item => item.country,
				...(shouldShowMultiStageJourneyOption && {
					buildOptions: () => {
						return [
							{
								value: "multi-stage-journey",
								label: formatMessage({
									id: "listing.filter.destination.multi.destinations.label",
								}),
							},
						];
					},
					customMatcher: product => {
						return [
							PRODUCT_EXPERIENCES.CIRCUIT,
							PRODUCT_EXPERIENCES.AUTOTOUR,
							PRODUCT_EXPERIENCES.CRUISE,
						].includes(product?.productExperience);
					},
				}),
				allowSearch: true,
				search: { placeholderKey: "filter.destination.searchbar.placeholder" },
			},
			{
				field: PRODUCTS_FILTERS_KEY.OFFER_TYPE,
				title: formatMessage({ id: "product.page.filter.offer.type.label" }),
				valueGetter: product => product.merchandisings,
				buildOptions: products => {
					const offerTypes = [
						...new Set(
							products
								.flatMap(product => product.merchandisings)
								.map(merchandising => {
									if (
										merchandising?.startsWith(PRODUCT_TAG_CODES.COUP_DE_COEUR)
									) {
										return PRODUCT_TAG_CODES.COUP_DE_COEUR;
									}
									if (merchandising?.startsWith(PRODUCT_TAG_CODES.GC)) {
										return PRODUCT_TAG_CODES.GC;
									}
									return null;
								})
								.filter(Boolean)
						),
					];

					return offerTypes.map(merchandising => {
						return {
							value: merchandising,
							labelKey: OFFER_TYPE_LABELS[merchandising] ?? merchandising,
						};
					});
				},
				customMatcher: (product, selectedValues) => {
					const productOfferTypes = product.merchandisings;

					return productOfferTypes?.some(offerType =>
						selectedValues?.some(selectedValue => offerType.startsWith(selectedValue))
					);
				},
				allowSingleOption: true,
				optionMode: OPTION_MODE.MANUAL_ONLY,
			},
			{
				field: PRODUCTS_FILTERS_KEY.MULTI_STAGE_JOURNEY,
				title: formatMessage({ id: "multi.stage.journey" }),
				valueGetter: product => product.productExperience,
				buildOptions: products => {
					return [...new Set(products.map(product => product.productExperience))]
						.filter(option => Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(option))
						.map(option => ({
							value: option,
							label: formatMessage({ id: MULTI_STAGE_JOURNEY_OPTIONS[option] }),
						}));
				},
				optionMode: OPTION_MODE.MANUAL_ONLY,
				allowSingleOption: true,
			},
			{
				field: PRODUCTS_FILTERS_KEY.TOPIC,
				title: formatMessage({ id: "listing.filter.theme.label" }),
				valueGetter: item => item.topics,
			},
			{
				field: PRODUCTS_FILTERS_KEY.MONTH,
				title: formatMessage({ id: "listing.filter.month.label" }),
				valueGetter: item => item.months.map(month => month.label),
				formatLabel: ({ label }) => {
					const [year, month] = label.split("/");
					const date = new Date(Number(year), Number(month) - 1, 1);
					return formatDate(date, { month: "long", year: "numeric" });
				},
				customSort: (a, b) => {
					return a?.value?.localeCompare(b?.value);
				},
			},
			{
				field: PRODUCTS_FILTERS_KEY.PROPERTY_NAME,
				title: formatMessage({ id: "product.page.filter.property.name.label" }),
				allowSearch: true,
				search: { placeholderKey: "filter.property.name.searchbar.placeholder" },
				valueGetter: product => product.id,
				buildOptions: products => {
					return products
						.filter(
							product =>
								!Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(
									product?.productExperience
								)
						)
						.map(product => ({ value: product.id, label: product.name }))
						.sort((a, b) => a.label.localeCompare(b.label));
				},
				optionMode: OPTION_MODE.MANUAL_ONLY,
				showCount: false,
			},
		],
		[shouldShowMultiStageJourneyOption, formatMessage, formatDate]
	);

	return (
		<FilterProvider facetConfigs={facetConfigs} products={productsOfLandingPage}>
			<SortProvider comparators={CUSTOM_COMPARATORS}>
				<div className="landing-page-seo">
					<LandingPageListing {...props} loading={loading} />
				</div>
			</SortProvider>
		</FilterProvider>
	);
};

LandingPageContainer.propTypes = {
	activeLandingPageCode: PropTypes.string,
	clearProduct: PropTypes.func,
	acceptCookyPolicy: PropTypes.func,
	resetAllProductsFilter: PropTypes.func,
	partnerCode: PropTypes.string,
	resolution: PropTypes.string,
	fetchLandingPage: PropTypes.func,
	clearBooking: PropTypes.func,
	listingSEO: PropTypes.object,
	updateSDPFormDestination: PropTypes.func,
	destinations: PropTypes.array,
	currentLandingPage: PropTypes.object,
	productsOfLandingPage: PropTypes.array,
};

const mapStateToProps = state => {
	const homeBlocks = getPartnerMarketingHomeBlocks(state);

	const hasMembershipBlock =
		homeBlocks?.length > 0 &&
		homeBlocks?.findIndex(block => block._type === SDP_SEARCH_MARKETING_BLOCKS.MEMBERSHIP) !==
			-1;

	const currentLandingPage = state.landingPage.current;

	const hideMembershipBlock = currentLandingPage?.displayOptions?.hideMembershipBlock;

	return {
		resolution: state.resolution,
		currentLandingPage: getCurrentLandingPage(state),
		shouldHideProductPriceBanner: shouldLandingPageHideProductPriceBanner(state),
		listingSEO: getPartnerListingSEO(state),
		showMembershipBlock: hasMembershipBlock && !hideMembershipBlock,
		landingPageMeta: getLandingPageMeta(state),
		activeLandingPageCode: state.landingPage.activeLandingPageCode,
		partnerCode: state.partner.code,
		productsOfLandingPage: getProductsOfLandingPage(state),
		destinations: state.smartDP.destinations,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			clearProduct,
			acceptCookyPolicy,
			resetAllProductsFilter,
			resetAllSDPProductsFilter,
			updateSDPFormDestination,
			fetchLandingPage,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(LandingPageContainer);
